import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import lt from './locales/lt'
import en from './locales/en'
import ru from './locales/ru'
import uk from './locales/uk'

export const SUPPORTED_LANGUAGES = {
  lt: { name: 'Lietuvių', nativeName: 'Lietuvių', flag: '🇱🇹' },
  en: { name: 'English', nativeName: 'English', flag: '🇬🇧' },
  ru: { name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  uk: { name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦' },
} as const

export type Language = keyof typeof SUPPORTED_LANGUAGES
export const DEFAULT_LANGUAGE: Language = 'lt'
export const LANGUAGE_STORAGE_KEY = 'lang'

export const languageStorage = {
  get: (): Language => {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY)
    if (saved && saved in SUPPORTED_LANGUAGES) {
      return saved as Language
    }
    return DEFAULT_LANGUAGE
  },
  set: (language: Language) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  },
}

export const LOCALE_MAP: Record<string, string> = {
  lt: 'lt-LT',
  en: 'en-GB',
  ru: 'ru-RU',
  uk: 'uk-UA',
}

/**
 * TODO: Consider migrating to proper i18next namespaces for lazy loading
 * Current setup uses flat structure: all translations nested under 'translation' key.
 * Components use useTranslation() and access keys as t('auth.keyName').
 * 
 * To enable lazy loading per namespace:
 * 1. Update this config to use separate namespace objects (not nested under translation)
 * 2. Add `ns: ['common', 'auth', 'dashboard', ...]` and `defaultNS: 'common'`
 * 3. Delete locale index.ts files, import JSONs directly
 * 4. Components can then use useTranslation('auth') with t('keyName')
 * 5. Optionally add i18next-http-backend for true lazy loading
 * 
 * TODO: Add i18next-cli for key extraction and validation
 * - npm install --save-dev i18next-cli
 * - Create i18next.config.ts with extract/types/lint settings
 * - Add scripts: i18n:extract, i18n:status, i18n:types, i18n:lint
 * - Use --ci flag in CI/CD to fail on missing translations
 * - Note: i18next-parser is deprecated as of Sept 2025, use i18next-cli instead
 */
i18n
  .use(initReactI18next)
  .init({
    resources: {
      lt: { ...lt },
      en: { ...en },
      ru: { ...ru },
      uk: { ...uk },
    },
    lng: languageStorage.get(),
    fallbackLng: DEFAULT_LANGUAGE,
    ns: [
      'common',
      'components',
      'navigation',
      'auth',
      'dashboard',
      'expenses',
      'income',
      'taxes',
      'kyc',
      'profile',
      'validation',
      'welcome',
      'bolt',
      'uber',
      'handbook',
      'tours',
      'calculator',
    ],
    interpolation: {
      escapeValue: false,
    },
    nsSeparator: '.',
    react: {
      useSuspense: false,
    },
  })

export default i18n
